import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Banner, Layout, Page } from '@shopify/polaris';
import { SkeletonPageCustom } from './../SkeletonPageCustom';

export function AppBridgeProvider({ children }) {
    const location = useLocation();

    // 1) Cache host (sticky across navigations / reloads in dev)
    const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const host = search.get('host') || window.__SHOPIFY_DEV_HOST || '';
    const shop = search.get('shop') || '';

    useEffect(() => {
        if (host) window.__SHOPIFY_DEV_HOST = host;
    }, [host]);

    // 2) Install/top-level redirect fallback (your old logic kept)
    const embedded = search.get('embedded');
    const hmac = search.get('hmac');
    const isTop = window.top === window.self;

    if ((!embedded || embedded !== '1') && hmac && isTop) {
        // during OAuth callback in a non-embedded context, bounce to your install endpoint
        (window.top ?? window).location.assign(`https://${window.location.host}/Account/Install${location.search}`);
        return <SkeletonPageCustom />;
    }

    // 3) Safety banners (same semantics you had)
    const apiKey =
        document.querySelector('meta[name="shopify-api-key"]')?.getAttribute('content') ||
        process.env.REACT_APP_SHOPIFY_API_KEY;

    if (!apiKey || !host) {
        const bannerProps = !apiKey
            ? {
                title: 'Missing Shopify API Key',
                children: (
                    <>
                        Your app is running without the <b>shopify-api-key</b> meta (or env). Add
                        <code> &lt;meta name="shopify-api-key" content="%REACT_APP_SHOPIFY_API_KEY%" /&gt; </code>
                        to <code>index.html</code>.
                    </>
                ),
            }
            : {
                title: 'Missing host query argument',
                children: (
                    <>
                        Your app URL must include a <b>host</b> parameter when embedded.
                        Use “Test your app” from Partners or keep the <code>host</code> when navigating.
                    </>
                ),
            };

        return (
            <Page narrowWidth>
                <Layout>
                    <Layout.Section>
                        <div style={{ marginTop: 100 }}>
                            <Banner {...bannerProps} status="critical" />
                        </div>
                    </Layout.Section>
                </Layout>
            </Page>
        );
    }

    return <>{children}</>;
}